<template>
  <v-list class="py-0">
    <v-list-item class="py-2" :prepend-gap="16">
      <template #prepend>
        <v-avatar size="40">
          <v-img :src="userAvatar" :alt="userFullName" />
        </v-avatar>
      </template>
      <v-list-item-title>{{ userFullName }}</v-list-item-title>
      <v-list-item-subtitle>{{ companyName }}</v-list-item-subtitle>
      <template #append>
        <v-btn
          icon="mdi-close"
          variant="text"
          size="small"
          aria-label="Close"
          data-test="profile-menu-close"
          @click="emit('close')"
        />
      </template>
    </v-list-item>
    <!-- the compact top bar puts its notifications row here -->
    <slot />
    <v-divider />
    <v-list-item :to="getAccountRoute({ name: 'user-profile' })" @click="emit('close')">
      <template #prepend>
        <v-icon>mdi-account-circle</v-icon>
      </template>
      <v-list-item-title>Profile</v-list-item-title>
    </v-list-item>
    <template v-if="!isClient">
      <v-divider />
      <v-list-item :to="getAccountRoute({ name: 'administration-root-page' })" @click="emit('close')">
        <template #prepend>
          <v-icon>mdi-cog</v-icon>
        </template>
        <v-list-item-title>Account Settings</v-list-item-title>
      </v-list-item>
    </template>
    <template v-if="activeAccount && userAccounts.length > 1">
      <v-divider />
      <v-list-item to="/my-accounts" @click="emit('close')">
        <template #prepend>
          <v-icon>mdi-vector-combine</v-icon>
        </template>
        <v-list-item-title>{{ activeAccount.name }}</v-list-item-title>
        <v-list-item-subtitle>Click here to switch accounts</v-list-item-subtitle>
      </v-list-item>
    </template>
    <v-divider />
    <ThemeModeToggle />
    <!-- BETA-CLEANUP: the feedback and switch-back rows -->
    <template v-if="isBetaApp">
      <v-divider />
      <v-list-item data-test="beta-feedback" @click="openBetaFeedback">
        <template #prepend>
          <v-icon>mdi-message-alert-outline</v-icon>
        </template>
        <v-list-item-title>Report a problem with the new version</v-list-item-title>
      </v-list-item>
      <v-divider />
      <v-list-item
        data-test="leave-beta"
        data-track="beta.current-app.open"
        :disabled="isLeavingBeta"
        @click="switchBack"
      >
        <template #prepend>
          <v-progress-circular
            v-if="isLeavingBeta"
            indeterminate
            size="24"
            width="2"
            data-test="leave-beta-loader"
          />
          <v-icon v-else>mdi-swap-horizontal</v-icon>
        </template>
        <v-list-item-title>Switch back to the old version</v-list-item-title>
      </v-list-item>
    </template>
    <v-divider />
    <v-list-item data-test="profile-menu-logout" @click="logout">
      <template #prepend>
        <v-icon>mdi-logout</v-icon>
      </template>
      <v-list-item-title>Logout</v-list-item-title>
    </v-list-item>
  </v-list>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { useStore } from 'vuex';

import { IUserAccount } from '@/bundles/User/interfaces';
import { RootActions } from '@/store/types/action-types';
import { useAccountRoute } from '@/bundles/App/composables/useAccountRoute';
import { useEventBus } from '@/bundles/App/composables/useEventBus';
import { useNotifications } from '@/bundles/App/composables/useNotifications';
import { usePlaceholders } from '@/bundles/App/composables/usePlaceholders';
import { useUserRoles } from '@/bundles/App/composables/useUserRoles';
import { isBetaApp, leaveBeta } from '@/bundles/App/helpers/betaChannel'; // BETA-CLEANUP

import ThemeModeToggle from '@/bundles/Panels/components/ThemeModeToggle.vue';

interface IEmits {
  close: [];
}

const emit = defineEmits<IEmits>();

const store = useStore();
const { getAccountRoute } = useAccountRoute();
const { emit: emitEvent } = useEventBus();
const { addNotification } = useNotifications();
const { contactPlaceholder } = usePlaceholders();
const { isClient } = useUserRoles();

const userFullName = computed<string>(() => store.state.userName);
const companyName = computed<string>(() => store.state.company);
const userAvatar = computed<string>(() => store.state.user?.profile?.img || contactPlaceholder.value);
const userAccounts = computed<IUserAccount[]>(() => store.state.user?.account ?? []);
const activeAccount = computed<IUserAccount | undefined>(() => store.state.user?.active_account);

// BETA-CLEANUP: the existing request form, opened on a bug report that names the beta and the page it came from
const openBetaFeedback = async (): Promise<void> => {
  emit('close');
  emitEvent('open-request-form');
  await nextTick();
  emitEvent('open-bug-report');
  emitEvent('fill-bug-report', {
    name: '[New version] ',
    message: '',
    url: window.location.href,
  });
};

// BETA-CLEANUP: busy while leaveBeta saves the choice; it resolves as the page starts navigating away
const isLeavingBeta = ref(false);

const switchBack = async (): Promise<void> => {
  if (isLeavingBeta.value) return;

  isLeavingBeta.value = true;

  try {
    await leaveBeta();
  } catch (error: any) {
    addNotification({ ...error, status: 0 });
  } finally {
    isLeavingBeta.value = false;
  }
};

const logout = async (): Promise<void> => {
  try {
    await store.dispatch(RootActions.LOG_OUT);
  } catch (error: any) {
    addNotification({ ...error });
  }
};
</script>
